import React, { useState, useEffect, useRef } from 'react';
import { formatTime12h } from '../shared/availability';
import { CreditCard, DollarSign, Gift, Receipt, TrendingUp, Check, X, Plus, Calendar, Users, ShoppingCart, Search, Trash2, PackagePlus, Scissors, User } from 'lucide-react';
import toast from 'react-hot-toast';
import { appointmentService, Appointment } from '../services/appointmentService';
import aiSettings from '../services/AISettingsService';
import { CheckoutValidationResult } from '../services/checkoutInterceptor';
import { clientDataService, Client } from '../services/clientDataService';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import discountApprovalService from '../services/discountApprovalService';
import { shopSettingsService } from '../services/shopSettingsService';
import { barberDataService, Barber } from '../services/barberDataService';
import { inventoryService } from '../services/inventoryService';
import type { Product as FirebaseProduct } from '../types/inventory';
import { flatServiceDataService } from '../services/flatServiceDataService';
import { reportingDataService } from '../services/reportingDataService';
import { useTheme, elementStyle } from '../theme/themeRuntime';
import { SmartCheckoutModal } from './SmartCheckoutModal';

interface CartItem {
  id: string;
  type: 'service' | 'product' | 'giftcard';
  name: string;
  price: number;
  quantity: number;
  barberId?: string;
  barberName?: string;
  category?: string;
  imageUrl?: string;
  recipientName?: string; // gift cards only — who it's for
}

interface CheckoutClient {
  id: string;
  name: string;
  phone: string;
  barberId?: string;
  barberName?: string;
}

interface Transaction {
  id: string;
  date: string;
  clients: CheckoutClient[];
  items: CartItem[];
  subtotal: number;
  tip: number;
  tax: number;
  total: number;
  paymentMethod: 'cash' | 'card' | 'mobile' | 'giftcard' | 'slik-coins';
  status: 'pending' | 'completed' | 'refunded' | 'partial-refund';
  slikCoinsEarned: number;
  slikCoinsUsed: number;
}

interface PaymentStats {
  dailyRevenue: number;
  weeklyRevenue: number;
  monthlyRevenue: number;
  averageTransaction: number;
  tipPercentage: number;
  topPaymentMethod: string;
}

const PaymentsModule: React.FC = () => {
  // Theme runtime (Jose 2026-09-07 Phase 2) — hero + primary button read the owner's tone choices.
  const theme = useTheme();
  // Checkout state
  const [activeCheckout, setActiveCheckout] = useState<Transaction | null>(null);
  const [checkoutClients, setCheckoutClients] = useState<CheckoutClient[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  // Unified checkout: the cart hands off to the shared SmartCheckoutModal (Jose 2026-08-13).
  const [showSmartCheckout, setShowSmartCheckout] = useState(false);
  const [smartCheckoutAppt, setSmartCheckoutAppt] = useState<any>(null);

  // UI state
  const [showServiceMenu, setShowServiceMenu] = useState(false);
  const [showProductMenu, setShowProductMenu] = useState(false);
  const [showAppointmentSearch, setShowAppointmentSearch] = useState(false);
  const [showGroupCheckout, setShowGroupCheckout] = useState(false);
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [showBarberSelector, setShowBarberSelector] = useState(false);
  const [selectedServiceCategory, setSelectedServiceCategory] = useState<string>('all');
  const [selectedProductCategory, setSelectedProductCategory] = useState<string>('all');

  // Gift Card state
  const [giftCardAmount, setGiftCardAmount] = useState<number>(50);
  const [customGiftCardAmount, setCustomGiftCardAmount] = useState<string>('');
  const [purchasingGiftCard, setPurchasingGiftCard] = useState(false);

  // Pending item (waiting for barber selection)
  const [pendingItem, setPendingItem] = useState<{
    type: 'service' | 'product';
    id: string;
    name: string;
    price: number;
    category: string;
    imageUrl?: string;
  } | null>(null);

  // AI Features state
  const [aiSettingsState, setAiSettingsState] = useState(aiSettings.getSettings());

  // Fraud Detection state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [checkoutValidation, setCheckoutValidation] = useState<CheckoutValidationResult | null>(null);

  // Appointment search
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentSearchQuery, setAppointmentSearchQuery] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');

  // Walk-in customer name (editable)
  // Empty by default so the grayed "Walk-in Customer…" PLACEHOLDER shows (you type a name straight in,
  // no need to clear a prefilled value). An empty name falls back to "Walk-in Customer" at checkout.
  const [walkInCustomerName, setWalkInCustomerName] = useState('');

  // Client autocomplete state
  const [clientSuggestions, setClientSuggestions] = useState<Client[]>([]);
  const [showClientAutocomplete, setShowClientAutocomplete] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);


  // Discount reasons (loaded from settings)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [barberStrikes, setBarberStrikes] = useState<number>(0);
  const [discountReasons, setDiscountReasons] = useState<{ id: string; label: string }[]>([]);

  // Barbers from Firebase
  const [barbers, setBarbers] = useState<Barber[]>([]);

  // Products from Firebase
  const [firebaseProducts, setFirebaseProducts] = useState<FirebaseProduct[]>([]);
  // Register services now come from the LIVE service brain (flatServiceDataService → Firestore
  // 'service_categories') — the SAME catalog the appointment flow + public site use. Was a hardcoded
  // mock list that ignored the shop's real services. (Jose 2026-08-13, unify the register on brains)
  const [firebaseServices, setFirebaseServices] = useState<Array<{ id: string; name: string; price: number; category: string }>>([]);

  // REAL STATS (Jose 2026-08-18): the Register header was hardcoded fake numbers (2840/18500/72000/65/18/Card).
  // Now sourced from reportingDataService — the same engine that drives Reports/payroll — so it reflects truth.
  const [stats, setStats] = useState<PaymentStats>({
    dailyRevenue: 0,
    weeklyRevenue: 0,
    monthlyRevenue: 0,
    averageTransaction: 0,
    tipPercentage: 0,
    topPaymentMethod: '—'
  });

  // Load appointments on mount
  useEffect(() => {
    const loadAppointments = () => {
      const allAppointments = appointmentService.getAllAppointments();
      setAppointments(allAppointments);
    };

    loadAppointments();
    const unsubscribe = appointmentService.subscribe(loadAppointments);
    return unsubscribe;
  }, []);

  // Load REAL revenue stats for the header (mount + after every completed checkout — keyed on the
  // SmartCheckout modal closing so a fresh sale updates Today/Week/Month immediately).
  useEffect(() => {
    let cancelled = false;
    const loadStats = async () => {
      try {
        const now = new Date();
        const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const todayStr = ymd(now);
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay()); // Sunday-start week
        const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        // Headline Today/Week/Month come from the BLENDED money spine (reconciled Fresha history +
        // live SLIK checkouts, no double-count) — same number the Reports/Overview show, so the uploaded
        // Fresha data is reflected here too. `daily` still drives the SLIK-register payment-method mix.
        const [daily, blendToday, blendWeek, blendMonth] = await Promise.all([
          reportingDataService.getDailySummary(todayStr),
          reportingDataService.getBlendedRevenue(todayStr, todayStr),
          reportingDataService.getBlendedRevenue(ymd(weekStart), todayStr),
          reportingDataService.getBlendedRevenue(monthStart, todayStr),
        ]);
        if (cancelled) return;
        const pm: any = (daily as any).payments || {};
        const methods: Array<[string, number]> = [
          ['Cash', pm.cash || 0], ['Card', pm.card || 0], ['Mobile', pm.mobilePay || 0], ['Store Credit', pm.storeCredit || 0],
        ];
        const top = methods.reduce((a, b) => (b[1] > a[1] ? b : a), ['—', 0] as [string, number]);
        const mBase = blendMonth.service + blendMonth.product;
        setStats({
          dailyRevenue: blendToday.total,
          weeklyRevenue: blendWeek.total,
          monthlyRevenue: blendMonth.total,
          averageTransaction: blendMonth.tickets > 0 ? Math.round(blendMonth.total / blendMonth.tickets) : 0,
          tipPercentage: mBase > 0 ? Math.round((blendMonth.tips / mBase) * 100) : 0,
          topPaymentMethod: top[1] > 0 ? top[0] : '—',
        });
      } catch (e) {
        console.error('Register stats load failed:', e);
      }
    };
    loadStats();
    return () => { cancelled = true; };
  }, [showSmartCheckout]);

  // Load barbers from Firebase
  useEffect(() => {
    const unsubscribe = barberDataService.subscribe((firebaseBarbers) => {
      // Only include active barbers
      const activeBarbers = firebaseBarbers.filter(b => b.status === 'active');
      setBarbers(activeBarbers);
    });
    return unsubscribe;
  }, []);

  // Load retail products from the SAME source as the Inventory module (inventory_products) —
  // sellable = active + has a retail price. Jose 2026-09-03: retail was reading an empty 'products'
  // collection and falling back to emoji demos; this wires it to the real inventory.
  useEffect(() => {
    const load = () => setFirebaseProducts(
      inventoryService.getAllProducts().filter((p) => p.isActive !== false && (p.retailPrice ?? 0) > 0)
    );
    const unsubscribe = inventoryService.subscribeToProducts(load);
    load();
    return unsubscribe;
  }, []);

  // Load the shop's REAL services (live brain) + re-load on any catalog change.
  useEffect(() => {
    const load = () => flatServiceDataService.getFlatServiceList(true)
      .then(list => setFirebaseServices(list.map(s => ({ id: s.id, name: s.name, price: s.price, category: s.categoryName }))))
      .catch(() => setFirebaseServices([]));
    load();
    const unsubscribe = flatServiceDataService.subscribe(() => load());
    return unsubscribe;
  }, []);

  // AI Settings listener
  useEffect(() => {
    const handleSettingsChange = () => {
      setAiSettingsState(aiSettings.getSettings());
    };
    aiSettings.on('settingsChanged', handleSettingsChange);
    return () => {
      aiSettings.off('settingsChanged', handleSettingsChange);
    };
  }, []);

  // Load discount reasons from shop settings
  useEffect(() => {
    const loadDiscountReasons = async () => {
      try {
        const reasons = await shopSettingsService.getDiscountReasons();
        setDiscountReasons(reasons.map(r => ({ id: r.id, label: r.label })));
      } catch (error) {
        console.error('Failed to load discount reasons:', error);
        // Fallback to defaults if loading fails
        setDiscountReasons([
          { id: 'service_issue', label: 'Service Issue' },
          { id: 'unsatisfied_client', label: 'Unsatisfied Client' },
          { id: 'loyalty', label: 'Loyalty Reward' },
          { id: 'grandfather_pricing', label: 'Grandfather Pricing' },
          { id: 'other', label: 'Other' }
        ]);
      }
    };

    loadDiscountReasons();
  }, []);


  // Click outside to close autocomplete
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
        setShowClientAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle client name input change with search
  const handleClientNameChange = (value: string) => {
    setWalkInCustomerName(value);
    setSelectedClient(null);

    // Search for clients if input has at least 2 characters
    if (value.trim().length >= 2) {
      const results = clientDataService.searchClients(value.trim());
      setClientSuggestions(results);
      setShowClientAutocomplete(results.length > 0);
    } else {
      setClientSuggestions([]);
      setShowClientAutocomplete(false);
    }
  };

  // Handle selecting a client from suggestions
  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    setWalkInCustomerName(`${client.firstName} ${client.lastName}`);
    setClientSuggestions([]);
    setShowClientAutocomplete(false);
    toast.success(`✅ Selected existing client: ${client.firstName} ${client.lastName}`);
  };

  // Color palette for barber cards
  const barberColors = [
    'from-purple-500 to-indigo-600',
    'from-blue-500 to-cyan-600',
    'from-green-500 to-emerald-600',
    'from-orange-500 to-red-600',
    'from-pink-500 to-rose-600',
    'from-teal-500 to-cyan-600',
    'from-amber-500 to-orange-600',
    'from-violet-500 to-purple-600',
  ];

  // Transform Firebase barbers into card format
  const availableBarbers = barbers.map((barber, index) => {
    const initials = `${barber.firstName.charAt(0)}${barber.lastName.charAt(0)}`.toUpperCase();
    const color = barber.colorCode || barberColors[index % barberColors.length];
    const specialty = barber.skills?.[0] || barber.role || 'Barber';

    return {
      id: barber.id,
      name: barber.firstName,
      fullName: `${barber.firstName} ${barber.lastName}`,
      initials,
      color,
      specialty,
      rating: barber.avgRating || 0,
      photo: barber.photo,
    };
  });

  // Services + their categories come from the LIVE catalog (firebaseServices), not a hardcoded list.
  const availableServices = firebaseServices;
  const serviceCategories = ['All', ...Array.from(new Set(firebaseServices.map(s => s.category).filter(Boolean)))];

  // Product category chips derived from the REAL products so the filter always matches (Jose 2026-09-03).
  const productCategories = ['All', ...Array.from(new Set(firebaseProducts.map((p) => p.category).filter(Boolean)))];

  // Real retail products from inventory — no demo/emoji fallback (empty means empty, not fake).
  const availableProducts = firebaseProducts.map((p) => ({
    id: p.id,
    name: p.name,
    price: p.retailPrice || 0,
    category: p.category as string,
    imageUrl: p.imageUrl || '',
    hasImage: !!p.imageUrl,
  }));

  // Filtered services and products by category
  const filteredServices = selectedServiceCategory === 'All' || selectedServiceCategory === 'all'
    ? availableServices
    : availableServices.filter(s => s.category === selectedServiceCategory);

  const filteredProducts = selectedProductCategory === 'All' || selectedProductCategory === 'all'
    ? availableProducts
    : availableProducts.filter(p => p.category === selectedProductCategory);

  // Cart management - Open barber selector before adding
  const initiateServiceAdd = (service: { id: string; name: string; price: number; category: string }) => {
    setPendingItem({
      type: 'service',
      id: service.id,
      name: service.name,
      price: service.price,
      category: service.category
    });
    setShowBarberSelector(true);
  };

  const initiateProductAdd = (product: { id: string; name: string; price: number; category: string; imageUrl?: string }) => {
    setPendingItem({
      type: 'product',
      id: product.id,
      name: product.name,
      price: product.price,
      category: product.category,
      imageUrl: product.imageUrl
    });
    setShowBarberSelector(true);
  };

  // Add item with barber assigned
  const addToCartWithBarber = (barberId: string, barberName: string) => {
    if (!pendingItem) return;

    const itemWithBarber: CartItem = {
      id: `${pendingItem.id}-${Date.now()}`,
      type: pendingItem.type,
      name: `${pendingItem.name} (${barberName})`,
      price: pendingItem.price,
      quantity: 1,
      barberId,
      barberName,
      category: pendingItem.category,
      imageUrl: pendingItem.imageUrl
    };

    setCartItems([...cartItems, itemWithBarber]);
    setShowBarberSelector(false);
    setPendingItem(null);
    toast.success(`➕ ${pendingItem.name} added → ${barberName} gets commission`);
  };

  const removeFromCart = (itemId: string) => {
    setCartItems(cartItems.filter(item => item.id !== itemId));
    toast.success('🗑️ Item removed from cart');
  };

  const updateQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCartItems(cartItems.map(item =>
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    ));
  };

  // Resolve a display name for a barberId — mirrors AppointmentModal.getBarberName so "From Appt" behaves
  // like the Link button: never a blank/"Unknown" barber, which would collapse the per-barber tip-split
  // downstream (barberId || primary). Prefer the stored name, else the live barber directory.
  const resolveBarberName = (barberId?: string, fallbackName?: string): string => {
    if (fallbackName && fallbackName.trim()) return fallbackName.trim();
    if (barberId) {
      const inState = barbers.find(b => b.id === barberId);
      if (inState) return `${inState.firstName || ''} ${inState.lastName || ''}`.trim() || 'Unknown Barber';
      const rec = barberDataService.getBarberById(barberId);
      if (rec) return `${rec.firstName || ''} ${rec.lastName || ''}`.trim() || 'Unknown Barber';
    }
    return 'Unknown Barber';
  };

  // Load appointment into checkout - supports multiple appointments for commission tracking
  const loadAppointment = (appointment: Appointment) => {
    const barberName = resolveBarberName(appointment.barberId, appointment.barberName);
    // Check if this client is already in the checkout
    const clientExists = checkoutClients.find(c => c.id === appointment.clientId);

    if (!clientExists) {
      const client: CheckoutClient = {
        id: appointment.clientId,
        name: appointment.clientName,
        phone: appointment.clientPhone,
        barberId: appointment.barberId,
        barberName
      };
      setCheckoutClients([...checkoutClients, client]);
    }

    // Add service with barber already assigned from appointment
    const itemWithBarber: CartItem = {
      id: `appt-${appointment.id}-${Date.now()}`,
      type: 'service',
      name: `${appointment.service} (${barberName})`,
      price: appointment.price,
      quantity: 1,
      barberId: appointment.barberId,
      barberName,
      category: 'Appointment'
    };

    setCartItems([...cartItems, itemWithBarber]);
    toast.success(`📅 Added ${appointment.clientName} → ${appointment.service} with ${barberName}`);
  };

  // Add client to group checkout
  const addClientToGroup = () => {
    if (!clientSearchQuery.trim()) return;

    const newClient: CheckoutClient = {
      id: `client-${Date.now()}`,
      name: clientSearchQuery,
      phone: '+1234567890'
    };

    setCheckoutClients([...checkoutClients, newClient]);
    setClientSearchQuery('');
    toast.success(`👥 ${newClient.name} added to group checkout`);
  };

  const removeClientFromGroup = (clientId: string) => {
    setCheckoutClients(checkoutClients.filter(c => c.id !== clientId));
    toast.success('👥 Client removed from group');
  };

  // Start new checkout
  // UNIFIED CHECKOUT (Jose 2026-08-13): the Register no longer has its own inline checkout — Start
  // Checkout flattens the cart into a checkout object and hands off to the ONE shared SmartCheckoutModal
  // (same discounts, per-service fraud gate, tip, receipt, celebration + Rebook as the appointment path).
  const startNewCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('❌ Please add items to cart first');
      return;
    }

    // Customer: an existing client picked from the search, else the typed/blank walk-in name.
    const clientName = selectedClient
      ? `${selectedClient.firstName} ${selectedClient.lastName}`
      : (walkInCustomerName.trim() || 'Walk-in Customer');

    // Flatten the cart. The first SERVICE item is the headline (its barber gets the service credit);
    // extra services + products ride along as additionalItems (qty-expanded so inventory + totals are
    // exact). A product/gift-card-only sale has no service → a $0 "Retail Sale" primary, totals from
    // additionalItems. (v1 credits the primary barber; per-item split attribution is a follow-up.)
    const firstService = cartItems.find(i => i.type === 'service');
    const primaryBarberId = firstService?.barberId || cartItems[0]?.barberId;
    const primaryBarberName = firstService?.barberName || cartItems[0]?.barberName;

    const additionalItems: Array<{ type: 'service' | 'product' | 'giftcard'; name: string; price: number; productId?: string; recipientName?: string; barberId?: string; barberName?: string }> = [];
    cartItems.forEach(item => {
      const units = item.quantity || 1;
      const rideAlong = (firstService && item.id === firstService.id) ? units - 1 : units;
      for (let k = 0; k < rideAlong; k++) {
        additionalItems.push({
          type: item.type,
          name: item.name,
          price: item.price,
          productId: item.type === 'product' ? item.id : undefined,
          recipientName: item.type === 'giftcard' ? item.recipientName : undefined,
          // PER-BARBER CREDIT: carry each cart item's barber (from the Barber-Selector) so a product
          // rung on another barber's ticket credits the barber who actually sold it at checkout.
          barberId: item.barberId,
          barberName: item.barberName,
        });
      }
    });

    setSmartCheckoutAppt({
      clientId: selectedClient?.id,
      clientName,
      clientPhone: selectedClient?.phone,
      clientEmail: (selectedClient as any)?.email,
      serviceName: firstService ? firstService.name : 'Retail Sale',
      price: firstService ? firstService.price : 0,
      barberId: primaryBarberId,
      barberName: primaryBarberName,
      additionalItems,
    });
    setShowSmartCheckout(true);
  };

  // Reset the whole register after a completed sale (or cancel).
  const resetRegisterAfterCheckout = () => {
    setShowSmartCheckout(false);
    setSmartCheckoutAppt(null);
    setCartItems([]);
    setCheckoutClients([]);
    setSelectedClient(null);
    setWalkInCustomerName('');
    setActiveCheckout(null);
  };

  // Purchase gift card
  // Gift card = a CART LINE, not an instant purchase (Jose 2026-08-13). It rings up through the ONE
  // unified checkout — alone (walk in JUST for a gift card) OR alongside services/products. The real
  // card is issued on payment (SmartCheckoutModal). No barber (deferred value, no commission).
  const handleGiftCardPurchase = () => {
    const finalAmount = customGiftCardAmount ? parseFloat(customGiftCardAmount) : giftCardAmount;
    if (isNaN(finalAmount) || finalAmount < 25) {
      toast.error('Minimum gift card amount is $25');
      return;
    }
    const recipient = walkInCustomerName.trim();
    setCartItems(prev => [...prev, {
      id: `giftcard-${Date.now()}`,
      type: 'giftcard',
      name: `🎁 Gift Card${recipient ? ` — ${recipient}` : ''}`,
      price: finalAmount,
      quantity: 1,
      recipientName: recipient || undefined,
    }]);
    toast.success(`🎁 $${finalAmount.toFixed(2)} gift card added to cart`);
    setCustomGiftCardAmount('');
    setGiftCardAmount(50);
    setShowGiftCardModal(false);
  };


  // Filter appointments for search
  const _seenAppt = new Set<string>();
  const filteredAppointments = appointments
    .filter(apt => apt.status === 'scheduled' || apt.status === 'confirmed' || apt.status === 'checked-in')
    .filter(apt => {
      const q = appointmentSearchQuery.toLowerCase();
      return (apt.clientName || '').toLowerCase().includes(q) ||
             (apt.barberName || '').toLowerCase().includes(q) ||
             (apt.service || '').toLowerCase().includes(q);
    })
    // Dedup identical appointments (same client/barber/day/time/service) — matches the Link-picker fix,
    // so a stale-cache double never shows twice in "From Appt".
    .filter(apt => {
      const key = `${apt.clientId || apt.clientName}|${apt.barberId}|${apt.date}|${apt.time}|${apt.service}`;
      if (_seenAppt.has(key)) return false;
      _seenAppt.add(key);
      return true;
    });



  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex-1 p-4 md:p-6 overflow-y-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">Register</h1>
          <p className="text-gray-600 dark:text-gray-400">Complete point-of-sale checkout system</p>
        </div>

        {/* Revenue Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="bg-white dark:bg-gray-800 p-3 md:p-4 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs md:text-sm">Today</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">${stats.dailyRevenue.toLocaleString()}</p>
              </div>
              <DollarSign className="h-6 w-6 md:h-8 md:w-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 md:p-4 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs md:text-sm">This Week</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">${stats.weeklyRevenue.toLocaleString()}</p>
              </div>
              <TrendingUp className="h-6 w-6 md:h-8 md:w-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 md:p-4 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs md:text-sm">This Month</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">${stats.monthlyRevenue.toLocaleString()}</p>
              </div>
              <TrendingUp className="h-6 w-6 md:h-8 md:w-8 text-purple-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 md:p-4 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs md:text-sm">Avg Transaction</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">${stats.averageTransaction}</p>
              </div>
              <Receipt className="h-6 w-6 md:h-8 md:w-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 md:p-4 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs md:text-sm">Avg Tip</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{stats.tipPercentage}%</p>
              </div>
              <Gift className="h-6 w-6 md:h-8 md:w-8 text-pink-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 md:p-4 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs md:text-sm">Top Method</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{stats.topPaymentMethod}</p>
              </div>
              <CreditCard className="h-6 w-6 md:h-8 md:w-8 text-indigo-500" />
            </div>
          </div>
        </div>

        {/* Checkout Builder - Only show when no active checkout */}
        {!activeCheckout && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden mb-6 md:mb-8">
            {/* Hero reads the owner's 'heroes' tone (Theme Studio, Phase 2). */}
            <div className="p-4 md:p-6" style={elementStyle('heroes', theme.brand.hero)}>
              <h2 className="text-xl md:text-2xl font-bold">Build Checkout</h2>
              <p className="mt-1 text-sm md:text-base opacity-80">Add services, products, or load from appointment</p>
            </div>

            <div className="p-4 md:p-6">
              {/* Quick Actions */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-6">
                <button
                  onClick={() => {
                    setShowServiceMenu(!showServiceMenu);
                    setShowProductMenu(false);
                    setShowAppointmentSearch(false);
                    setShowGroupCheckout(false);
                    setShowGiftCardModal(false);
                  }}
                  className={`flex items-center justify-center gap-2 p-3 md:p-4 rounded-lg border-2 transition-all ${
                    showServiceMenu ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <ShoppingCart className="w-4 h-4 md:w-5 md:h-5 text-gray-700 dark:text-gray-300" />
                  <span className="font-medium text-sm md:text-base text-gray-900 dark:text-white">Add Service</span>
                </button>

                <button
                  onClick={() => {
                    setShowProductMenu(!showProductMenu);
                    setShowServiceMenu(false);
                    setShowAppointmentSearch(false);
                    setShowGroupCheckout(false);
                    setShowGiftCardModal(false);
                  }}
                  className={`flex items-center justify-center gap-2 p-3 md:p-4 rounded-lg border-2 transition-all ${
                    showProductMenu ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <PackagePlus className="w-4 h-4 md:w-5 md:h-5 text-gray-700 dark:text-gray-300" />
                  <span className="font-medium text-sm md:text-base text-gray-900 dark:text-white">Add Product</span>
                </button>

                <button
                  onClick={() => {
                    setShowAppointmentSearch(!showAppointmentSearch);
                    setShowServiceMenu(false);
                    setShowProductMenu(false);
                    setShowGroupCheckout(false);
                    setShowGiftCardModal(false);
                  }}
                  className={`flex items-center justify-center gap-2 p-3 md:p-4 rounded-lg border-2 transition-all ${
                    showAppointmentSearch ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-gray-700 dark:text-gray-300" />
                  <span className="font-medium text-sm md:text-base text-gray-900 dark:text-white">From Appt</span>
                </button>

                <button
                  onClick={() => {
                    setShowGiftCardModal(!showGiftCardModal);
                    setShowServiceMenu(false);
                    setShowProductMenu(false);
                    setShowAppointmentSearch(false);
                    setShowGroupCheckout(false);
                  }}
                  className={`flex items-center justify-center gap-2 p-3 md:p-4 rounded-lg border-2 transition-all ${
                    showGiftCardModal ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <Gift className="w-4 h-4 md:w-5 md:h-5 text-gray-700 dark:text-gray-300" />
                  <span className="font-medium text-sm md:text-base text-gray-900 dark:text-white">Gift Cards</span>
                </button>
              </div>

              {/* Service Selection Menu */}
              {showServiceMenu && (
                <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/30 rounded-lg border-2 border-purple-200 dark:border-purple-700">
                  <h3 className="font-semibold mb-3 flex items-center text-gray-900 dark:text-white">
                    <Scissors className="w-5 h-5 mr-2" />
                    Select Services
                  </h3>

                  {/* Category Filter */}
                  <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                    {serviceCategories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setSelectedServiceCategory(cat)}
                        className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                          selectedServiceCategory === cat
                            ? 'bg-purple-600 text-white'
                            : 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-600 hover:bg-purple-100 dark:hover:bg-purple-900/30'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {filteredServices.map(service => (
                      <button
                        key={service.id}
                        onClick={() => initiateServiceAdd(service)}
                        className="flex flex-col items-start p-4 bg-white dark:bg-gray-700 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors border-2 border-purple-200 dark:border-purple-600 hover:border-purple-400"
                      >
                        <div className="flex items-center justify-between w-full mb-2">
                          <span className="font-semibold text-gray-900 dark:text-white">{service.name}</span>
                          <Scissors className="w-4 h-4 text-purple-400" />
                        </div>
                        <div className="flex items-center justify-between w-full">
                          <span className="text-xs text-gray-500 dark:text-gray-400">{service.category}</span>
                          <span className="font-bold text-purple-600 dark:text-purple-400">${service.price}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Product Selection Menu */}
              {showProductMenu && (
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border-2 border-green-200 dark:border-green-700">
                  <h3 className="font-semibold mb-3 flex items-center text-gray-900 dark:text-white">
                    <PackagePlus className="w-5 h-5 mr-2" />
                    Select Products
                  </h3>

                  {/* Category Filter */}
                  <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                    {productCategories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setSelectedProductCategory(cat)}
                        className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                          selectedProductCategory === cat
                            ? 'bg-green-600 text-white'
                            : 'bg-white dark:bg-gray-700 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-600 hover:bg-green-100 dark:hover:bg-green-900/30'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {filteredProducts.map(product => (
                      <button
                        key={product.id}
                        onClick={() => initiateProductAdd(product)}
                        className="flex flex-col items-center p-4 bg-white dark:bg-gray-700 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors border-2 border-green-200 dark:border-green-600 hover:border-green-400 group"
                      >
                        {product.hasImage ? (
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            className="w-14 h-14 md:w-16 md:h-16 rounded-lg object-cover mb-2 shadow group-hover:scale-105 transition-transform"
                          />
                        ) : (
                          <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg bg-gradient-to-br from-green-100 to-emerald-200 dark:from-green-900/50 dark:to-emerald-900/50 flex items-center justify-center mb-2 group-hover:scale-105 transition-transform">
                            <span className="text-2xl md:text-3xl font-bold text-green-700 dark:text-green-300">{(product.name || '?').charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400 mb-1">{product.category}</span>
                        <span className="font-semibold text-sm text-center text-gray-900 dark:text-white line-clamp-2">{product.name}</span>
                        <span className="font-bold text-green-600 dark:text-green-400 mt-1">${product.price}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Appointment Search */}
              {showAppointmentSearch && (
                <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border-2 border-blue-200 dark:border-blue-700">
                  <h3 className="font-semibold mb-3 flex items-center text-gray-900 dark:text-white">
                    <Calendar className="w-5 h-5 mr-2" />
                    Load from Appointment
                  </h3>
                  <div className="mb-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search appointments..."
                        value={appointmentSearchQuery}
                        onChange={(e) => setAppointmentSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400"
                      />
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {filteredAppointments.length === 0 ? (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-4">No appointments found</p>
                    ) : (
                      filteredAppointments.map(apt => (
                        <button
                          key={apt.id}
                          onClick={() => loadAppointment(apt)}
                          className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors border border-blue-200 dark:border-blue-600"
                        >
                          <div className="text-left">
                            <p className="font-semibold text-gray-900 dark:text-white">{apt.clientName}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{apt.service} with {resolveBarberName(apt.barberId, apt.barberName)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-blue-600 dark:text-blue-400">${Number(apt.price || 0).toFixed(2)}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{formatTime12h(apt.time)}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Gift Card Purchase */}
              {showGiftCardModal && (
                <div className="mb-6 p-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-200 shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold flex items-center text-purple-900">
                      <Gift className="w-6 h-6 mr-2" />
                      Purchase Gift Card
                    </h3>
                    <button
                      onClick={() => setShowGiftCardModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Gift Card Preview */}
                  <div className="mb-6 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl p-6 text-white shadow-xl">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-white/80 text-sm">Gift Card Value</p>
                        <p className="text-4xl font-bold">
                          ${(customGiftCardAmount ? parseFloat(customGiftCardAmount) || 0 : giftCardAmount).toFixed(2)}
                        </p>
                      </div>
                      <Gift className="w-16 h-16 text-white/30" />
                    </div>
                    <p className="text-white/70 text-sm">Valid for 12 months • Redeemable at SLIK</p>
                  </div>

                  {/* Amount Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">Select Amount</label>
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      {[25, 50, 75, 100, 150, 200].map((amount) => (
                        <button
                          key={amount}
                          onClick={() => {
                            setGiftCardAmount(amount);
                            setCustomGiftCardAmount('');
                          }}
                          className={`p-3 rounded-lg font-semibold text-lg transition-all ${
                            giftCardAmount === amount && !customGiftCardAmount
                              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg scale-105'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500'
                          }`}
                        >
                          ${amount}
                        </button>
                      ))}
                    </div>

                    {/* Custom Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Or enter custom amount (min $25)
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg font-semibold">
                          $
                        </span>
                        <input
                          type="number"
                          value={customGiftCardAmount}
                          onChange={(e) => setCustomGiftCardAmount(e.target.value)}
                          placeholder="Enter amount"
                          min={25}
                          className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-semibold focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Recipient Name (uses walk-in customer field) */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recipient Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={walkInCustomerName}
                      onChange={(e) => setWalkInCustomerName(e.target.value)}
                      placeholder="Gift card recipient"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  {/* Purchase Button */}
                  <button
                    onClick={handleGiftCardPurchase}
                    disabled={purchasingGiftCard}
                    className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold text-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {purchasingGiftCard ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Gift className="w-5 h-5" />
                        Add Gift Card to Cart
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Group Checkout */}
              {showGroupCheckout && (
                <div className="mb-6 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Group Checkout - Add Multiple Clients
                  </h3>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      placeholder="Enter client name..."
                      value={clientSearchQuery}
                      onChange={(e) => setClientSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addClientToGroup()}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <button
                      onClick={addClientToGroup}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add
                    </button>
                  </div>
                  {checkoutClients.length > 0 && (
                    <div className="space-y-2">
                      {checkoutClients.map(client => (
                        <div key={client.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-yellow-200">
                          <span className="font-medium">{client.name}</span>
                          <button
                            onClick={() => removeClientFromGroup(client.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Current Cart */}
              {cartItems.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Cart ({cartItems.length} items)
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    {cartItems.map(item => {
                      // Check if this is an appointment service (has barber name in parentheses)
                      const hasBarberName = item.name.includes('(') && item.name.includes(')');
                      const isService = item.type === 'service';

                      return (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-lg border-l-4"
                             style={{ borderLeftColor: isService ? '#9333ea' : '#10b981' }}>
                          <div className="flex-1">
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-gray-600">
                              {isService ? '🔧 Service' : '📦 Product'}
                              {hasBarberName && <span className="ml-2 text-purple-600 font-medium">• Commission Tracked</span>}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                              >
                                -
                              </button>
                              <span className="w-8 text-center font-semibold">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                              >
                                +
                              </button>
                            </div>
                            <span className="font-bold w-16 text-right">${(item.price * item.quantity).toFixed(2)}</span>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Clients Summary */}
              {checkoutClients.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Clients ({checkoutClients.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {checkoutClients.map(client => (
                      <div key={client.id} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                        {client.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Walk-in Customer Name Input with Autocomplete (only shown when no clients added via group checkout) */}
              {checkoutClients.length === 0 && cartItems.length > 0 && (
                <div className="mb-6 relative" ref={autocompleteRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Customer Name (Optional)
                    {selectedClient && (
                      <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        ✓ Existing Client
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={walkInCustomerName}
                      onChange={(e) => handleClientNameChange(e.target.value)}
                      onFocus={() => {
                        if (clientSuggestions.length > 0) {
                          setShowClientAutocomplete(true);
                        }
                      }}
                      placeholder="Walk-in Customer or search existing clients..."
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        selectedClient ? 'border-green-400 bg-green-50' : 'border-gray-300'
                      }`}
                    />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {selectedClient
                      ? `Selected: ${selectedClient.phone} • ${selectedClient.email}`
                      : 'Start typing to search for existing clients or enter a new name'
                    }
                  </p>

                  {/* Autocomplete Dropdown */}
                  {showClientAutocomplete && clientSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {clientSuggestions.slice(0, 5).map((client) => (
                        <button
                          key={client.id}
                          onClick={() => handleSelectClient(client)}
                          className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">
                                {client.firstName} {client.lastName}
                                {client.membershipType !== 'regular' && (
                                  <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                                    client.membershipType === 'vip' ? 'bg-purple-100 text-purple-700' :
                                    client.membershipType === 'premium' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}>
                                    {client.membershipType.toUpperCase()}
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-gray-600">{client.phone}</p>
                              <p className="text-xs text-gray-500">{client.email}</p>
                            </div>
                            <div className="text-right ml-4">
                              <p className="text-xs text-gray-500">{client.totalVisits} visits</p>
                              <p className="text-xs font-semibold text-green-600">${(client.totalSpent || 0).toFixed(0)} spent</p>
                            </div>
                          </div>
                        </button>
                      ))}
                      {clientSuggestions.length > 5 && (
                        <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 text-center">
                          Showing 5 of {clientSuggestions.length} results. Keep typing to narrow down.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Start Checkout Button */}
              <button
                onClick={startNewCheckout}
                disabled={cartItems.length === 0}
                style={elementStyle('buttonsPrimary', theme.brand.primary)}
                className="w-full px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                Start Checkout ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''}{checkoutClients.length > 0 ? `, ${checkoutClients.length} client${checkoutClients.length !== 1 ? 's' : ''}` : ''})
              </button>
            </div>
          </div>
        )}

        {/* Barber Selector Modal */}
        {showBarberSelector && pendingItem && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
            {/* max-h + scroll so a long barber grid can't clip off-screen on a short display. (Jose 2026-10-23) */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">Select Barber</h2>
                    <p className="text-purple-100 mt-1">
                      Who gets commission for <strong>{pendingItem.name}</strong>?
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowBarberSelector(false);
                      setPendingItem(null);
                    }}
                    className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-4 md:p-6">
                {availableBarbers.length === 0 ? (
                  <div className="text-center py-8">
                    <User className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600 dark:text-gray-400 mb-2">No barbers found</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">Add barbers in Staff Management to assign services</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                    {availableBarbers.map(barber => (
                      <button
                        key={barber.id}
                        onClick={() => addToCartWithBarber(barber.id, barber.name)}
                        className="flex flex-col items-center p-4 md:p-6 bg-white dark:bg-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-all border-2 border-gray-200 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-lg group"
                      >
                        {barber.photo ? (
                          <img
                            src={barber.photo}
                            alt={barber.fullName}
                            className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover mb-3 shadow-lg group-hover:scale-110 transition-transform border-4 border-white dark:border-gray-600"
                          />
                        ) : (
                          <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br ${barber.color} flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform`}>
                            <span className="text-xl md:text-2xl font-bold text-white">{barber.initials}</span>
                          </div>
                        )}
                        <span className="text-lg md:text-xl font-bold text-gray-800 dark:text-white">{barber.name}</span>
                        <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">{barber.specialty}</span>
                        {barber.rating > 0 && (
                          <div className="flex items-center gap-1 mt-2">
                            <span className="text-yellow-500">★</span>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{barber.rating.toFixed(1)}</span>
                          </div>
                        )}
                        <div className="mt-3 px-3 py-1 bg-green-100 dark:bg-green-900/50 rounded-full">
                          <span className="text-xs md:text-sm text-green-700 dark:text-green-400 font-medium">
                            +${pendingItem?.price || 0} commission
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                  <div className="flex items-start">
                    <User className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold mb-1 text-blue-900 dark:text-blue-300">Commission Tracking</p>
                      <p className="text-blue-700 dark:text-blue-400">
                        Select which barber performed this {pendingItem?.type === 'service' ? 'service' : 'sold this product'}.
                        They will receive commission credit for ${pendingItem?.price?.toFixed(2) || '0.00'}.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* UNIFIED CHECKOUT (Jose 2026-08-13) — Start Checkout hands the cart to the ONE shared
            SmartCheckoutModal. No `id` on the checkout object ⇒ it creates a completed WALK-IN sale on
            payment (reporting/payroll/inventory identical to an appointment checkout). */}
        <SmartCheckoutModal
          appointment={smartCheckoutAppt}
          isOpen={showSmartCheckout}
          onClose={() => { setShowSmartCheckout(false); setSmartCheckoutAppt(null); }}
          onPaymentComplete={() => { resetRegisterAfterCheckout(); }}
        />
      </div>
    </div>
  );
};

export default PaymentsModule;
